import { useState, useEffect } from 'react';
import { BaseButton, BaseInput } from '../../base';
import { Box, Typography, RadioGroup, Radio, Switch, FormControlLabel } from '@mui/material';
import { useTheme } from '@mui/material';
import { numberToTomans } from '../../../utils/helpers';
import useProtectedClient from '../../../hooks/useProtectedClient';
import { updateProductInMarket } from '../../../api/admin/adminMarketApi';
import { useNotification } from '../../../context/NotificationProvider';

const ProductSettings = ({marketId, product}) => {
    const theme = useTheme();
    const [buttonLoading, setButtonLoading] = useState(false)
    const [isActive, setIsActive] = useState(product.isActive)
    const [buyActive, setBuyActive] = useState(product.buyActive)
    const [buyBasePrice, setBuyBasePrice] = useState(product.buyBasePrice)
    const [buyOffsetPrice, setBuyOffsetPrice] = useState(product.buyOffsetPrice)
    const [buyFeePercentage, setBuyFeePercentage] = useState(product.buyFeePercentage)
    const [isBuyPriceOnline, setIsBuyPriceOnline] = useState(product.isBuyPriceOnline)
    const [isSellPriceOnline, setIsSellPriceOnline] = useState(product.isSellPriceOnline)
    const [sellActive, setSellActive] = useState(product.sellActive)
    const [sellBasePrice, setSellBasePrice] = useState(product.sellBasePrice)
    const [sellOffsetPrice, setSellOffsetPrice] = useState(product.sellOffsetPrice)
    const protectedClient = useProtectedClient();
    const {showNotification} = useNotification();

    useEffect(() => {
      setIsActive(product.isActive);
    }, [product.isActive]);

    useEffect(() => {
      setBuyActive(product.buyActive);
    }, [product.buyActive]);

    useEffect(() => {
      setBuyBasePrice(product.buyBasePrice);
    }, [product.buyBasePrice]);

    useEffect(() => {
      setBuyOffsetPrice(product.buyOffsetPrice);
    }, [product.buyOffsetPrice]);

    useEffect(() => {
      setBuyFeePercentage(product.buyFeePercentage);
    }, [product.buyFeePercentage]);
    
    useEffect(() => {
      setIsBuyPriceOnline(product.isBuyPriceOnline);
    }, [product.isBuyPriceOnline]);

    useEffect(() => {
      setIsSellPriceOnline(product.isSellPriceOnline);
    }, [product.isSellPriceOnline]);

    useEffect(() => {
      setSellActive(product.sellActive);
    }, [product.sellActive]);

    useEffect(() => {
      setSellBasePrice(product.sellBasePrice);
    }, [product.sellBasePrice]);

    useEffect(() => {
      setSellOffsetPrice(product.sellOffsetPrice);
    }, [product.sellOffsetPrice]);

    const submitProduct = async () => {
        setButtonLoading(true);
        try {
            const res = await updateProductInMarket(protectedClient, marketId, {
                productId: product.productId, 
                isActive,
                buyActive,
                buyBasePrice,
                buyOffsetPrice: Number(buyOffsetPrice),
                buyFeePercentage: Number(buyFeePercentage),
                isBuyPriceOnline,
                isSellPriceOnline,
                sellActive,
                sellBasePrice,
                sellOffsetPrice: Number(sellOffsetPrice)
            })
            showNotification(res.message, "success")
        } catch (error) {
            showNotification(error, "error");
        } finally {
            setButtonLoading(false);
        }
    }
    const handleChangeProductActive = (event) => {
        setIsActive(event.target.checked);
    };
    const handleChangeBuyActive = (event) => {
        setBuyActive(event.target.checked);
    };
    const handleIsOnlinePriceChangeBuy = (event) => {
        setIsBuyPriceOnline(event.target.value === "buyOnline")
    }
    const handleChangeSellActive = (event) => {
        setSellActive(event.target.checked);
    };
    const handleIsOnlinePriceChangeSell = (event) => {
        setIsSellPriceOnline(event.target.value === "sellOnline")
    }
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                padding: '10px',
                background: theme.palette.background.default
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    width: '100%',
                    background: theme.palette.background.paper
                }}
            >
                <Switch
                    checked={isActive}
                    onChange={handleChangeProductActive}
                    name="status"
                    color="primary"
                    sx={{
                        transform: 'scale(0.8)',
                    }}
                />
                <Typography
                    variant='caption'
                >
                    {product.productName}
                </Typography>
                <BaseButton
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        marginRight: '8px',
                        borderRadius: '50px',
                        width: '30%',
                        height: '25px',
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                    }}
                    isLoading={buttonLoading}
                    onClick={submitProduct}
                >
                    <Typography variant='caption2'>
                        بروزرسانی
                    </Typography>
                </BaseButton>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                    gap: '4px',
                    paddingTop: '6px'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '10%',
                        gap: '4px',
                        padding: "20px",
                        background: theme.palette.background.harmony
                    }}
                >
                    <Typography
                        variant='subtitle'
                    >
                        خرید
                    </Typography>
                    <Switch
                        checked={buyActive}
                        onChange={handleChangeBuyActive}
                        name="status"
                        color="primary"
                        sx={{
                            transform: 'scale(0.8)', // Adjust the scale to make it smaller
                        }}
                    />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        gap: '4px',
                        padding: '4px',
                        border: '1px solid',
                        borderColor: 'white',
                        borderRadius: '4px',
                        background: theme.palette.background.harmony
                    }}
                >
                    <RadioGroup
                        row // Use row for horizontal alignment, remove for vertical alignment
                        value={isBuyPriceOnline ? "buyOnline": "buyOffline"}
                        onChange={handleIsOnlinePriceChangeBuy}
                        sx={{
                            // gap: '16px',
                        }}
                    >
                        <FormControlLabel
                            value="buyOnline"
                            control={<Radio />}
                            label="پایه آنلاین"
                            sx={{
                                width: '100%',
                                margin: 0,
                                '& .MuiFormControlLabel-label': 
                                {
                                    fontSize: '12px',
                                },
                            }}
                        />
                        <Typography
                            variant='subtitle'
                            sx={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center', justifyContent: 'center', width: '100%'}}
                        >
                            {`${numberToTomans(product.buyPriceOnline)}`}
                        </Typography>
                        <FormControlLabel
                            value="buyOffline"
                            control={<Radio />}
                            label="پایه دستی"
                            sx={{ 
                                width: '100%',
                                margin: 0,
                                '& .MuiFormControlLabel-label': 
                                {
                                    fontSize: '12px',
                                },
                            }}
                        />
                        <BaseInput
                            label="قیمت"
                            value={buyBasePrice}
                            onChange={(e) => setBuyBasePrice(e.target.value)}
                            name="basepricebuy"
                            type="number"
                            sx={{
                                '& input': {
                                    padding: '4px',
                                },
                                '& .MuiInputLabel-root': {
                                    display: 'none', // Hide the label
                                },
                            }}
                        />
                    </RadioGroup>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        width: '100%',
                        padding: '20px 4px',
                        border: '1px solid',
                        borderColor: 'white',
                        borderRadius: '4px',
                        background: theme.palette.background.harmony
                    }}
                >
                    <BaseInput
                        label="آفست ثابت"
                        value={buyOffsetPrice}
                        onChange={(e) => setBuyOffsetPrice(e.target.value)}
                        name="offsetpricebuy"
                        type="number"
                        sx={{
                            margin: "8px 0"
                        }}
                    />
                    <BaseInput
                        label="درصد سود"
                        value={buyFeePercentage}
                        onChange={(e) => setBuyFeePercentage(e.target.value)}
                        name="offsetpricebuy"
                        type="number"
                        sx={{
                            margin: "8px 0"
                        }}
                    />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        gap: '4px',
                        padding: '4px',
                        border: '1px solid',
                        borderColor: 'white',
                        borderRadius: '4px',
                        background: theme.palette.background.harmony
                    }}
                >
                    <Typography
                        variant='subtitle'
                        sx={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center', justifyContent: 'center', textAlign: 'center', width: '100%'}}
                    >
                        قیمت نهایی خرید
                    </Typography>
                    <Typography
                        variant='subtitle2'
                        sx={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center', justifyContent: 'center', width: '100%'}}
                    >
                        (تومان)
                    </Typography>
                    <Typography
                        variant='subtitle'
                        sx={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center', justifyContent: 'center', width: '100%'}}
                    >
                        {`${numberToTomans(Number(buyOffsetPrice) + Math.round(Number(((isBuyPriceOnline ? product.buyPriceOnline : buyBasePrice)) * buyFeePercentage)))}`}
                    </Typography>
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                    gap: '4px',
                    paddingTop: '6px',
                    background: theme.palette.background.harmony
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '10%',
                        gap: '4px',
                        padding: "20px",
                        background: theme.palette.background.harmony
                    }}
                >
                    <Typography
                        variant='subtitle'
                    >
                        فروش
                    </Typography>
                    <Switch
                        checked={sellActive}
                        onChange={handleChangeSellActive}
                        name="status"
                        color="primary"
                        sx={{
                            transform: 'scale(0.8)',
                        }}
                    />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        gap: '4px',
                        padding: '4px',
                        border: '1px solid',
                        borderColor: 'white',
                        borderRadius: '4px',
                    }}
                >
                    <RadioGroup
                        row
                        value={isSellPriceOnline ? "sellOnline": "sellOffline"}
                        onChange={handleIsOnlinePriceChangeSell}
                    >
                        <FormControlLabel
                            value="sellOnline"
                            control={<Radio />}
                            label="پایه آنلاین"
                            sx={{
                                width: '100%',
                                margin: 0,
                                '& .MuiFormControlLabel-label': 
                                {
                                    fontSize: '12px',
                                },
                            }}
                        />
                        <Typography
                            variant='subtitle'
                            sx={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center', justifyContent: 'center', width: '100%'}}
                        >
                            {`${new Intl.NumberFormat().format(product.sellPriceOnline)}`}
                        </Typography>
                        <FormControlLabel
                            value="sellOffline"
                            control={<Radio />}
                            label="پایه دستی"
                            sx={{ 
                                width: '100%',
                                margin: 0,
                                '& .MuiFormControlLabel-label': 
                                {
                                    fontSize: '12px',
                                },
                            }}
                        />
                        <BaseInput
                            label="قیمت"
                            value={sellBasePrice}
                            onChange={(e) => setSellBasePrice(e.target.value)}
                            name="basepricesell"
                            type="number"
                            sx={{
                                '& input': {
                                    padding: '4px',
                                },
                                '& .MuiInputLabel-root': {
                                    display: 'none',
                                },
                            }}
                        />
                    </RadioGroup>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        width: '100%',
                        padding: '20px 4px',
                        border: '1px solid',
                        borderColor: 'white',
                        borderRadius: '4px',
                    }}
                >
                    <BaseInput
                        label="آفست ثابت"
                        value={sellOffsetPrice}
                        onChange={(e) => setSellOffsetPrice(e.target.value)}
                        name="offsetpricesell"
                        type="number"
                    />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        gap: '4px',
                        padding: '4px',
                        border: '1px solid',
                        borderColor: 'white',
                        borderRadius: '4px',
                    }}
                >
                    <Typography
                        variant='subtitle'
                        sx={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center', justifyContent: 'center', textAlign: 'center', width: '100%'}}
                    >
                         قیمت نهایی فروش
                    </Typography>
                    <Typography
                        variant='subtitle2'
                        sx={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center', justifyContent: 'center', width: '100%'}}
                    >
                        (تومان)
                    </Typography>
                    <Typography
                        variant='subtitle'
                        sx={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center', justifyContent: 'center', width: '100%'}}
                    >
                        {`${numberToTomans(Number(sellOffsetPrice) + Math.round(Number(isSellPriceOnline ? product.sellPriceOnline : sellBasePrice)))}`}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default ProductSettings;
